import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

const RECOVERY_SESSION_KEY = 'pairtab.recovery.session';
const getHashParams = () => new URLSearchParams(window.location.hash.replace(/^#/, ''));
const getIntent = (flow: string | null, type: string | null) => {
  if (flow === 'recovery') return 'recovery';
  if (flow === 'signup') return 'signup';
  if (type === 'recovery') return 'recovery';
  return 'signup';
};

export default function AuthCallback() {
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;

    const handleAuthCallback = async () => {
      const url = new URL(window.location.href);
      const hashParams = getHashParams();
      const searchParams = url.searchParams;

      const flow = searchParams.get('flow') || hashParams.get('flow');
      const type = searchParams.get('type') || hashParams.get('type');
      const code = searchParams.get('code');
      const tokenHash = searchParams.get('token_hash') || hashParams.get('token_hash');
      const accessToken = searchParams.get('access_token') || hashParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token') || hashParams.get('refresh_token');
      const intent = getIntent(flow, type);
      let session: { access_token: string; refresh_token: string } | null = null;

      try {
        if (accessToken && refreshToken) {
          session = {
            access_token: accessToken,
            refresh_token: refreshToken,
          };

          const { error } = await supabase.auth.setSession(session);
          if (error) throw error;
        } else if (code) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
          session = data.session
            ? {
                access_token: data.session.access_token,
                refresh_token: data.session.refresh_token,
              }
            : null;
        } else if (tokenHash && type) {
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: type as 'signup' | 'recovery' | 'invite' | 'email_change' | 'email' | 'magiclink',
          });
          if (error) throw error;
          session = data.session
            ? {
                access_token: data.session.access_token,
                refresh_token: data.session.refresh_token,
              }
            : null;
        } else {
          throw new Error('We could not verify that link. Please request a new one.');
        }

        if (!active) return;

        if (intent === 'recovery') {
          if (session?.access_token && session?.refresh_token) {
            window.sessionStorage.setItem(RECOVERY_SESSION_KEY, JSON.stringify(session));
          }

          navigate('/login?mode=update-password', { replace: true });
          return;
        }

        navigate('/login?confirmed=1', { replace: true });
      } catch (err: any) {
        if (!active) return;
        const message = encodeURIComponent(err?.message || 'We could not verify that link. Please try again.');
        navigate(`/login?authError=${message}`, { replace: true });
      }
    };

    handleAuthCallback();

    return () => {
      active = false;
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="w-full max-w-sm rounded-3xl border border-border bg-card/70 p-8 text-center"
      >
        <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-primary" />
        <h1 className="text-xl font-semibold text-foreground">Finishing sign-in</h1>
        <p className="mt-2 text-sm text-muted">
          Hold tight while we verify your link and bring you back into PairTab.
        </p>
      </motion.div>
    </div>
  );
}
